import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useDebounce } from 'react-use';

import { ARBITRAGE_QUERY_KEYS } from '@/entities/arbitrage';

import { ApiArbitrage, ApiCexPairs, ApiPairs } from 'api';
import { ROUTE_PATHS } from 'shared/consts/routes';
import { useTypedDispatch, useTypedSelector } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import {
  ArbitrageStableCheckingMethod,
  generalArbitrageSettingsSchema,
  IArbitrageCexPairSettings,
  IArbitrageDexPairSettings,
  IArbitrageGeneralSettings,
  pairCexArbitrageSettingsSchema,
  pairDexArbitrageSettingsSchema,
} from 'types/arbitrage';
import { ICexPair, IDexPair } from 'types/pairs';

import {
  validateCexPairSettings,
  validateDexPairSettings,
  validateGeneralSettings,
  validateSelectedCexPairs,
  validateSelectedDexPairs,
} from './validateForm';

export type IArbitrageForm = IArbitrageGeneralSettings & {
  cexes: ICexPair[];
  dexes: IDexPair[];
  cexForms: Record<number, IArbitrageCexPairSettings>;
  dexForms: Record<number, IArbitrageDexPairSettings>;
};

interface IArgs {
  onClose: () => void;
  onSuccess?: (form: IArbitrageForm) => void;
  projectId: number;
  mode: 'add' | 'create';
  cexPairsToAdd?: ICexPair[];
  dexPairsToAdd?: IDexPair[];
}

export enum EStep {
  cex_pairs = 'cex_pairs',
  dex_pairs = 'dex_pairs',
  cex_pair = 'cex_pair',
  dex_pair = 'dex_pair',
  general = 'general',
}

interface IStep {
  name: EStep;
  back: string;
  continue: string;
}

const firstStep: IStep = {
  name: EStep.cex_pairs,
  back: 'Back',
  continue: 'Continue',
};

const secondStep: IStep = {
  name: EStep.dex_pairs,
  back: 'Back',
  continue: 'Continue',
};

const INITIAL_CEX_PAIR_FORM: IArbitrageCexPairSettings = {
  accountId: undefined,
  arbitrable: true,
  subscribe_to_updates: true,
  allow_rebalancer_buy: true,
  allow_rebalancer_sell: true,
  check_balance: true,
  target_base: '0',
  reserved_balance_base: '0',
  reserved_balance_quote: '0',
  is_reserved_balance_base_percentage: false,
  is_target_base_percentage: false,
};

const INITIAL_DEX_PAIR_FORM: IArbitrageDexPairSettings = {
  slippage_percent: '0',
  walletId: undefined,
  arbitrable: true,
  subscribe_to_updates: true,
  allow_rebalancer_buy: true,
  allow_rebalancer_sell: true,
  check_balance: true,
  target_base: '0',
  reserved_balance_base: '0',
  reserved_balance_quote: '0',
  is_reserved_balance_base_percentage: false,
  is_target_base_percentage: false,
};

const useAddArbitrageModal = ({
  mode,
  onClose,
  onSuccess,
  projectId,
  cexPairsToAdd,
  dexPairsToAdd,
}: IArgs) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const queryClient = useQueryClient();
  const projects = useTypedSelector(store => store.projects.projects);

  const [steps, setSteps] = useState<IStep[]>([firstStep]);
  const [currentStepIdx, setCurrentStepIdx] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);

  const [arbitrageForm, setArbitrageForm] = useState({
    cexes: [] as ICexPair[],
    dexes: [] as IDexPair[],
    cexForms: {},
    dexForms: {},
  } as IArbitrageForm);

  const [isValidationSuccess, setIsValidationSuccess] = useState<boolean>(false);

  const [cexPairs, setCexPairs] = useState<ICexPair[]>([]);
  const [dexPairs, setDexPairs] = useState<IDexPair[]>([]);

  const selectedCexPairs = useMemo(() => arbitrageForm.cexes, [arbitrageForm]);
  const selectedDexPairs = useMemo(() => arbitrageForm.dexes, [arbitrageForm]);

  const onlyDexes = useMemo(
    () => mode === 'add' && dexPairsToAdd !== undefined,
    [mode, dexPairsToAdd],
  );
  const onlyCexes = useMemo(
    () => mode === 'add' && cexPairsToAdd !== undefined,
    [mode, cexPairsToAdd],
  );

  const currentCexPair = useMemo(() => {
    if (onlyDexes) return null;

    const offsetIdxes = onlyCexes ? 1 : 2;

    return currentStepIdx ? (selectedCexPairs[currentStepIdx - offsetIdxes] ?? null) : null;
  }, [onlyDexes, onlyCexes, selectedCexPairs, currentStepIdx]);

  const currentDexPair = useMemo(() => {
    if (onlyCexes) return null;

    const OFFSET = onlyDexes ? 1 : 2 + selectedCexPairs.length;

    return currentStepIdx ? (selectedDexPairs[currentStepIdx - OFFSET] ?? null) : null;
  }, [onlyCexes, onlyDexes, selectedCexPairs, selectedDexPairs, currentStepIdx]);

  const project = useMemo(
    () => (projects ?? []).find(project => project.id === projectId) ?? null,
    [projects, projectId],
  );

  const setSelectedCexPairs = useCallback(
    (pairsOrUpdater: ICexPair[] | ((prevPairs: ICexPair[]) => ICexPair[])) => {
      setArbitrageForm(v => {
        const prevPairs = v.cexes;
        const newSelectedPairs =
          typeof pairsOrUpdater === 'function'
            ? (pairsOrUpdater as (prevPairs: ICexPair[]) => ICexPair[])(prevPairs)
            : (pairsOrUpdater as ICexPair[]);

        return { ...v, cexes: newSelectedPairs };
      });
    },
    [],
  );

  const setSelectedDexPairs = useCallback(
    (pairsOrUpdater: IDexPair[] | ((prevPairs: IDexPair[]) => IDexPair[])) => {
      setArbitrageForm(v => {
        const prevPairs = v.dexes;
        const newSelectedPairs =
          typeof pairsOrUpdater === 'function'
            ? (pairsOrUpdater as (prevPairs: IDexPair[]) => IDexPair[])(prevPairs)
            : (pairsOrUpdater as IDexPair[]);

        return { ...v, dexes: newSelectedPairs };
      });
    },
    [],
  );

  const toggleAllPairs = useCallback(
    (type: 'cex' | 'dex') => {
      if (type === 'cex') {
        const allPairsActive = cexPairs.every(pair =>
          selectedCexPairs.some(selected => selected.id === pair.id),
        );

        if (allPairsActive) {
          setSelectedCexPairs([]);
        } else {
          setSelectedCexPairs([...cexPairs]);
        }
      }

      if (type === 'dex') {
        const allPairsActive = dexPairs.every(pair =>
          selectedDexPairs.some(selected => selected.id === pair.id),
        );

        if (allPairsActive) {
          setSelectedDexPairs([]);
        } else {
          setSelectedDexPairs([...dexPairs]);
        }
      }
    },
    [
      selectedCexPairs,
      selectedDexPairs,
      cexPairs,
      dexPairs,
      setSelectedCexPairs,
      setSelectedDexPairs,
    ],
  );

  const generalSettings = useFormik<IArbitrageGeneralSettings>({
    initialValues: useMemo(
      () => ({
        min_profit: '0',
        usd_threshold: '0',
        maximum_limit_usd: '0',
        default_cancel_timeout_sec: 600 * 1000,
        is_maximum_limited: false,
        stable_checking_method: ArbitrageStableCheckingMethod.check_default,
        close_position_automatically: false,
      }),
      [],
    ),
    enableReinitialize: true,
    onSubmit: () => {},
    validationSchema: generalArbitrageSettingsSchema,
  });

  const cexPairSettings = useFormik<IArbitrageCexPairSettings>({
    initialValues: INITIAL_CEX_PAIR_FORM,
    enableReinitialize: true,
    onSubmit: () => {},
    validationSchema: pairCexArbitrageSettingsSchema,
  });

  const dexPairSettings = useFormik<IArbitrageDexPairSettings>({
    initialValues: INITIAL_DEX_PAIR_FORM,
    enableReinitialize: true,
    onSubmit: () => {},
    validationSchema: pairDexArbitrageSettingsSchema,
  });

  useEffect(() => {
    if (currentCexPair) {
      const form = arbitrageForm.cexForms?.[currentCexPair.id] ?? INITIAL_CEX_PAIR_FORM;

      cexPairSettings.setValues(form, true);

      setIsValidationSuccess(validateCexPairSettings(form).success);
    }

    //eslint-disable-next-line
  }, [currentCexPair]);

  useEffect(() => {
    if (currentDexPair) {
      const form = arbitrageForm.dexForms?.[currentDexPair.id] ?? INITIAL_DEX_PAIR_FORM;

      dexPairSettings.setValues(form, true);

      setIsValidationSuccess(validateDexPairSettings(form).success);
    }

    //eslint-disable-next-line
  }, [currentDexPair]);

  const setupSteps = useCallback(
    ({ cexPairsCount, dexPairsCount }: { cexPairsCount: number; dexPairsCount: number }) => {
      const generalStep: IStep = {
        name: EStep.general,
        back:
          dexPairsCount === 0 || onlyCexes
            ? `Cex Pair ${cexPairsCount}/${cexPairsCount}`
            : `Dex Pair ${dexPairsCount}/${dexPairsCount}`,
        continue: 'Create arbitrage',
      };

      const cexPairFinalTitle =
        dexPairsCount !== 0 ? 'Continue' : mode === 'create' ? 'General settings' : 'Add pairs';
      const dexPairFinalTitle = mode === 'create' ? 'General settings' : 'Add pairs';

      const cexPairSteps = onlyDexes
        ? []
        : Array.from({ length: cexPairsCount })
            .fill(0)
            .map((_, idx) => ({
              name: EStep.cex_pair,
              back: idx == 0 ? 'Back' : `Cex pair ${idx}/${cexPairsCount}`,
              continue:
                idx === cexPairsCount - 1
                  ? cexPairFinalTitle
                  : `Cex pair ${idx + 1}/${cexPairsCount}`,
            }));

      const dexPairSteps = onlyCexes
        ? []
        : Array.from({ length: dexPairsCount })
            .fill(0)
            .map((_, idx) => ({
              name: EStep.dex_pair,
              back: idx == 0 ? 'Back' : `Dex pair ${idx}/${dexPairsCount}`,
              continue:
                idx === dexPairsCount - 1
                  ? dexPairFinalTitle
                  : `Dex pair ${idx + 1}/${dexPairsCount}`,
            }));

      const startSteps = onlyCexes
        ? [firstStep]
        : onlyDexes
          ? [secondStep]
          : [firstStep, secondStep];

      setSteps(
        [
          ...startSteps,
          ...cexPairSteps,
          ...dexPairSteps,
          mode === 'create' ? generalStep : null,
        ].filter(el => el !== null),
      );
    },
    [onlyCexes, onlyDexes, mode],
  );

  const handleCreateArbitrage = useCallback(async () => {
    if (!project) return;

    try {
      setLoading(true);

      const { isSuccess, data, errorMessage } = await ApiArbitrage.createArbitrageConfig({
        project_id: project.id,
        active: false,
        execute_cex: true,
        execute_dex: true,
        usd_threshold: Number(generalSettings.values.usd_threshold),
        maximum_limit_usd: Number(generalSettings.values.maximum_limit_usd),
        is_maximum_limited: generalSettings.values.is_maximum_limited,
        min_profit_percentage: Number(generalSettings.values.min_profit),
        default_cancel_timeout_sec: (generalSettings.values.default_cancel_timeout_sec ?? 0) / 1000,
        stable_checking_method: generalSettings.values.stable_checking_method,
        close_position_automatically: generalSettings.values.close_position_automatically,
        pairs_cex: arbitrageForm.cexes
          .map(cexPair => {
            const values = arbitrageForm.cexForms[cexPair.id];

            if (!values) return null;

            const {
              accountId,
              allow_rebalancer_buy,
              allow_rebalancer_sell,
              arbitrable,
              check_balance,
              reserved_balance_base,
              reserved_balance_quote,
              subscribe_to_updates,
              target_base,
              is_reserved_balance_base_percentage,
              is_target_base_percentage,
            } = values;

            return {
              account_id: accountId!,
              allow_rebalancer_buy: allow_rebalancer_buy!,
              allow_rebalancer_sell: allow_rebalancer_sell!,
              arbitrable: arbitrable!,
              check_balance: check_balance!,
              pair_id: cexPair.id,
              reserved_balance_base: is_reserved_balance_base_percentage
                ? ''
                : reserved_balance_base!,
              reserved_balance_quote: is_reserved_balance_base_percentage
                ? ''
                : reserved_balance_quote!,
              reserved_balance_percent: is_reserved_balance_base_percentage
                ? Number(reserved_balance_base!)
                : 0,
              subscribe_to_updates: subscribe_to_updates!,
              target_base: is_target_base_percentage ? '' : target_base!,
              target_base_quote_percent: is_target_base_percentage ? Number(target_base) : 0,
            };
          })
          .filter(el => el !== null),
        pairs_dex: arbitrageForm.dexes
          .map(dexPair => {
            const values = arbitrageForm.dexForms[dexPair.id];

            if (!values) return null;

            const {
              walletId,
              allow_rebalancer_buy,
              allow_rebalancer_sell,
              arbitrable,
              check_balance,
              reserved_balance_base,
              reserved_balance_quote,
              subscribe_to_updates,
              target_base,
              is_reserved_balance_base_percentage,
              is_target_base_percentage,
              slippage_percent,
            } = values;

            return {
              wallet_id: walletId!,
              slippage_percent: Number(slippage_percent),
              allow_rebalancer_buy: allow_rebalancer_buy!,
              allow_rebalancer_sell: allow_rebalancer_sell!,
              arbitrable: arbitrable!,
              check_balance: check_balance!,
              pair_id: dexPair.id,
              reserved_balance_base: is_reserved_balance_base_percentage
                ? ''
                : reserved_balance_base!,
              reserved_balance_quote: is_reserved_balance_base_percentage
                ? ''
                : reserved_balance_quote!,
              reserved_balance_percent: is_reserved_balance_base_percentage
                ? Number(reserved_balance_base!)
                : 0,
              subscribe_to_updates: subscribe_to_updates!,
              target_base: is_target_base_percentage ? '' : target_base!,
              target_base_quote_percent: is_target_base_percentage ? Number(target_base) : 0,
            };
          })
          .filter(el => el !== null),
      });

      if (isSuccess && data) {
        try {
          await queryClient.invalidateQueries({
            queryKey: ARBITRAGE_QUERY_KEYS.listByProject(project.id),
          });
          await queryClient.refetchQueries({
            queryKey: ARBITRAGE_QUERY_KEYS.listByProject(project.id),
          });
        } catch (error) {
          console.error('[useAddArbitrageModal] Failed to refresh arbitrage queries', error);
        }

        onClose();
        navigate(
          generatePath(ROUTE_PATHS.arbitrage, {
            projectId: project.id.toString(),
            arbitrageId: data.id.toString(),
          }),
        );

        dispatch(
          setAlertState({
            type: 'success',
            text: 'Successfully created new arbitrage project!',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      } else {
        dispatch(
          setAlertState({
            type: 'failed-img',
            text: errorMessage ?? 'Something went wrong',
            onClose: () => dispatch(dropAlertState()),
            onSubmit: () => {
              dispatch(dropAlertState());
            },
          }),
        );
      }
    } catch (error) {
      console.log('error: ', error);

      dispatch(
        setAlertState({
          type: 'failed-img',
          text: 'Something went wrong',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
          },
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [onClose, project, navigate, dispatch, queryClient, generalSettings.values, arbitrageForm]);

  const currentStepControls = useMemo(() => {
    const step = steps[currentStepIdx];
    const hasNext = !!steps[currentStepIdx + 1];

    const onForwardClick = () => {
      if (isValidationSuccess) {
        setCurrentStepIdx(v => v + 1);
      }
    };

    const setFormState = () => {
      if (currentCexPair) {
        setArbitrageForm(prevState => ({
          ...prevState,
          cexForms: {
            ...prevState.cexForms,
            [currentCexPair.id]: cexPairSettings.values,
          },
        }));
      }

      if (currentDexPair) {
        setArbitrageForm(prevState => ({
          ...prevState,
          dexForms: {
            ...prevState.dexForms,
            [currentDexPair.id]: dexPairSettings.values,
          },
        }));
      }
    };

    const onBackwardClick = () => {
      if (!isValidationSuccess && step.name !== EStep.general) {
        if (currentCexPair) {
          setArbitrageForm(v => {
            const old = { ...v };
            delete old.cexForms[currentCexPair.id];
            return old;
          });
        }

        if (currentDexPair) {
          setArbitrageForm(v => {
            const old = { ...v };
            delete old.dexForms[currentDexPair.id];
            return old;
          });
        }
      }

      setCurrentStepIdx(v => (v === 0 ? 0 : v - 1));
    };

    const enabledNext = isValidationSuccess && !loading;

    if (step.name === EStep.cex_pairs) {
      return {
        backwardControl: undefined,
        forwardControl: {
          disabled: !enabledNext,
          title: step.continue,
          onForwardClick,
        },
      };
    }

    if (step.name === EStep.dex_pairs) {
      return {
        backwardControl: {
          disabled: false,
          title: step.back,
          onBackwardClick,
        },
        forwardControl: {
          disabled: !enabledNext,
          title: step.continue,
          onForwardClick,
        },
      };
    }

    if (mode === 'add' && [EStep.cex_pair, EStep.dex_pair].includes(step.name) && !hasNext) {
      return {
        backwardControl: {
          disabled: false,
          title: step.back,
          onBackwardClick,
        },
        forwardControl: {
          disabled: !enabledNext,
          title: step.continue,
          onForwardClick: () => {
            if (currentCexPair || currentDexPair) {
              onSuccess?.({
                ...arbitrageForm,
                ...(step.name === EStep.cex_pair && currentCexPair
                  ? {
                      cexForms: {
                        ...arbitrageForm.cexForms,
                        [currentCexPair.id]: cexPairSettings.values,
                      },
                    }
                  : {}),
                ...(step.name === EStep.dex_pair && currentDexPair
                  ? {
                      dexForms: {
                        ...arbitrageForm.dexForms,
                        [currentDexPair.id]: dexPairSettings.values,
                      },
                    }
                  : {}),
              });
            }
          },
        },
      };
    }

    if (step.name === EStep.cex_pair || step.name === EStep.dex_pair) {
      return {
        backwardControl: {
          disabled: false,
          title: step.back,
          onBackwardClick,
        },
        forwardControl: {
          disabled: !enabledNext,
          title: step.continue,
          onForwardClick: () => {
            setFormState();
            onForwardClick();
          },
        },
      };
    }

    if (step.name === EStep.general) {
      return {
        backwardControl: {
          disabled: false,
          title: step.back,
          onBackwardClick,
        },
        forwardControl: {
          disabled: !enabledNext,
          title: step.continue,
          onForwardClick: async () => {
            await handleCreateArbitrage();
            onSuccess?.(arbitrageForm);
          },
        },
      };
    }

    return {
      backwardControl: {
        disabled: false,
        title: step.back,
        onBackwardClick,
      },
      forwardControl: {
        disabled: !enabledNext,
        title: step.continue,
        onForwardClick: () => {
          setFormState();
          onForwardClick();
        },
      },
    };
  }, [
    arbitrageForm,
    onSuccess,
    mode,
    currentCexPair,
    currentDexPair,
    currentStepIdx,
    steps,
    loading,
    isValidationSuccess,
    cexPairSettings.values,
    dexPairSettings.values,
    handleCreateArbitrage,
  ]);

  useEffect(() => {
    const getPairs = async () => {
      setCexPairs(cexPairsToAdd ?? []);
      setDexPairs(dexPairsToAdd ?? []);

      if (!project || mode === 'add') return;

      try {
        const cexIds = project.cexPairs.map(el => el.id);
        const dexIds = project.dexPairs.map(el => el.id);

        const pairs = await Promise.all([
          ...(!cexPairsToAdd
            ? cexIds.map(async id => (await ApiCexPairs.getCexPairById(id)).data)
            : []),
          ...(!dexPairsToAdd
            ? dexIds.map(async id => (await ApiPairs.getDexPairById(id)).data)
            : []),
        ]);

        const _cexPairs = pairs.filter(el => el && 'cex' in el) as ICexPair[];
        const _dexPairs = pairs.filter(el => el && 'dex' in el) as IDexPair[];

        if (!cexPairsToAdd) {
          setCexPairs(_cexPairs);
        }
        if (!dexPairsToAdd) {
          setDexPairs(_dexPairs);
        }
      } catch (error) {
        console.log(error);
      }
    };

    getPairs();
  }, [mode, cexPairsToAdd, dexPairsToAdd, project]);

  useDebounce(
    () => {
      const step = steps[currentStepIdx];

      if (step) {
        const isCexesSelected = validateSelectedCexPairs(arbitrageForm.cexes).success;
        const isDexesSelected = validateSelectedDexPairs(arbitrageForm.dexes).success;

        if (step.name === EStep.cex_pairs) {
          setIsValidationSuccess(onlyCexes ? isCexesSelected : true);
        } else if (step.name === EStep.dex_pairs) {
          setIsValidationSuccess(isCexesSelected || isDexesSelected);
        } else if (step.name === EStep.cex_pair) {
          setIsValidationSuccess(validateCexPairSettings(cexPairSettings.values).success);
        } else if (step.name === EStep.dex_pair) {
          setIsValidationSuccess(validateDexPairSettings(dexPairSettings.values).success);
        } else if (step.name === EStep.general) {
          setIsValidationSuccess(validateGeneralSettings(generalSettings.values).success);
        }
      }
    },
    50,
    [
      arbitrageForm,
      currentStepIdx,
      steps,
      generalSettings.values,
      cexPairSettings.values,
      dexPairSettings.values,
    ],
  );

  return {
    project,
    steps,
    currentStepIdx,
    currentCexPair,
    currentDexPair,
    cexPairs,
    dexPairs,
    selectedCexPairs,
    selectedDexPairs,
    currentStepControls,
    setupSteps,
    setSelectedCexPairs,
    setSelectedDexPairs,
    generalSettings,
    cexPairSettings,
    dexPairSettings,
    toggleAllPairs,
  };
};

export { useAddArbitrageModal };
